<template>
  <div class="p-4 configuration-page">
    <h2 class="page-title mb-4">Configuration</h2>
    <b-row>
      <!-- General -->
      <b-col md="6" lg="4" class="mb-4">
        <div class="category-card color-general h-100">
          <h5 class="category-title">General</h5>
          <b-list-group flush>
            <b-list-group-item :to="{ name: 'Asset' }" class="config-item">
              <i class="ri-ship-2-line item-icon"></i> Asset
            </b-list-group-item>
            <b-list-group-item :to="{ name: 'Device' }" class="config-item">
              <i class="ri-remote-control-line item-icon"></i> Device
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_landmark')" :to="{ name: 'Landmarks' }" class="config-item">
              <i class="ri-road-map-line item-icon"></i> Landmarks
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- User Management -->
      <b-col v-if="canRead('master_user') || canRead('master_role') || canRead('master_permission')" md="6" lg="4" class="mb-4">
        <div class="category-card color-user-management h-100">
          <h5 class="category-title">User Management</h5>
          <b-list-group flush>
            <b-list-group-item v-if="canRead('master_user')" :to="{ name: 'User' }" class="config-item">
              <i class="ri-user-line item-icon"></i> Users List
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_role')" :to="{ name: 'Role' }" class="config-item">
              <i class="ri-shield-user-line item-icon"></i> Role
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_permission')" :to="{ name: 'Permission' }" class="config-item">
              <i class="ri-key-2-line item-icon"></i> Permission
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- Company & Asset -->
      <b-col v-if="canRead('master_group_company') || canRead('master_company') || canRead('master_fuel_type')" md="6" lg="4" class="mb-4">
        <div class="category-card color-company-asset h-100">
          <h5 class="category-title">Company & Asset</h5>
          <b-list-group flush>
            <b-list-group-item v-if="canRead('master_group_company')" :to="{ name: 'Group' }" class="config-item">
              <i class="ri-group-2-line item-icon"></i> Group
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_company')" :to="{ name: 'Company' }" class="config-item">
              <i class="ri-building-4-line item-icon"></i> Company
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_fuel_type')" :to="{ name: 'MasterAssetType' }" class="config-item">
              <i class="ri-ship-line item-icon"></i> Asset Type
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- Sensors & Tanks -->
      <b-col v-if="canRead('master_sensor_type') || canRead('master_sensor_category') || canRead('master_tank_header')" md="6" lg="4" class="mb-4">
        <div class="category-card color-sensors-tanks h-100">
          <h5 class="category-title">Sensors & Tanks</h5>
          <b-list-group flush>
            <b-list-group-item v-if="canRead('master_sensor_type')" :to="{ name: 'MasterSensorType' }" class="config-item">
              <i class="ri-sensor-line item-icon"></i> Sensor Type
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_sensor_category')" :to="{ name: 'MasterSensorCategory' }" class="config-item">
              <i class="ri-file-list-3-line item-icon"></i> Sensor Category
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_tank_header')" :to="{ name: 'MasterTank' }" class="config-item">
              <i class="ri-database-2-line item-icon"></i> Tank Header/Detail
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- Bunkering & Fuel -->
      <b-col v-if="canRead('master_fuel_type') || canRead('master_bunker_location')" md="6" lg="4" class="mb-4">
        <div class="category-card color-bunkering-fuel h-100">
          <h5 class="category-title">Bunkering & Fuel</h5>
          <b-list-group flush>
            <b-list-group-item v-if="canRead('master_fuel_type')" :to="{ name: 'MasterFuelType' }" class="config-item">
              <i class="ri-gas-station-line item-icon"></i> Fuel Type
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_bunker_location')" :to="{ name: 'MasterBunkerLocation' }" class="config-item">
              <i class="ri-map-pin-line item-icon"></i> Bunker Location
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- System Configuration -->
      <b-col v-if="canRead('master_config_rpm') || canRead('master_config_ae') || canRead('master_config_tank')" md="6" lg="4" class="mb-4">
        <div class="category-card color-system-config h-100">
          <h5 class="category-title">System Configuration</h5>
          <b-list-group flush>
            <b-list-group-item v-if="canRead('master_config_rpm')" :to="{ name: 'MasterConfigRpm' }" class="config-item">
              <i class="ri-speed-up-line item-icon"></i> Config RPM
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_config_ae')" :to="{ name: 'MasterConfigAe' }" class="config-item">
              <i class="ri-sound-module-line item-icon"></i> Config AE
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_config_tank')" :to="{ name: 'MasterConfigTank' }" class="config-item">
              <i class="ri-database-line item-icon"></i> Config Tank
            </b-list-group-item>
            <b-list-group-item v-if="canRead('master_rpm_operation_header')" to="/master/rpm-classification" class="config-item">
               <i class="ri-settings-3-line item-icon"></i> RPM Classification
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

      <!-- Events -->
      <b-col md="6" lg="4" class="mb-4">
        <div class="category-card color-events h-100">
          <h5 class="category-title">Events</h5>
          <b-list-group flush>
            <b-list-group-item :to="{ name: 'MasterEvent' }" class="config-item">
              <i class="ri-alarm-warning-line item-icon"></i> Master Event
            </b-list-group-item>
            <b-list-group-item :to="{ name: 'MasterConfigEvent' }" class="config-item">
              <i class="ri-notification-badge-line item-icon"></i> Config Event
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>

    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ConfigurationPage',
  computed: {
    ...mapGetters({
      userAccess: 'auth/userAccess',
    }),
  },
  methods: {
    canRead(module) {
      if (!this.userAccess || !this.userAccess[module]) return false;
      return this.userAccess[module].includes('read');
    },
  },
};
</script>

<style scoped>
.configuration-page {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.page-title {
  color: #333;
  font-weight: 600;
}

.category-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.05);
  transition: all 0.3s ease;
  overflow: hidden;
  border-top: 5px solid transparent;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
}

.category-title {
  padding: 1rem 1.25rem;
  margin-bottom: 0;
  background-color: transparent;
  font-weight: 600;
  color: #495057;
}

.config-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  color: #212529;
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
  border: none;
}

.config-item:hover {
  background-color: #f8f9fa;
  color: #ff69b4; /* Use app's primary color for hover text */
}

.item-icon {
  font-size: 1.2rem;
  margin-right: 1rem;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.config-item:hover .item-icon {
  transform: scale(1.1);
  color: #fff;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

/* Application Color Palette */
/* User Management - Info */
.color-user-management { border-top-color: #5dade2; }
.color-user-management .item-icon { background-color: #eaf5fc; color: #5dade2; }
.config-item:hover .color-user-management .item-icon { background-color: #5dade2; }

/* Company & Asset - Success */
.color-company-asset { border-top-color: #2ecc71; }
.color-company-asset .item-icon { background-color: #eafaf1; color: #2ecc71; }
.config-item:hover .color-company-asset .item-icon { background-color: #2ecc71; }

/* Sensors & Tanks - Warning */
.color-sensors-tanks { border-top-color: #f1c40f; }
.color-sensors-tanks .item-icon { background-color: #fef9e7; color: #f1c40f; }
.config-item:hover .color-sensors-tanks .item-icon { background-color: #f1c40f; }

/* Bunkering & Fuel - Secondary */
.color-bunkering-fuel { border-top-color: #8e44ad; }
.color-bunkering-fuel .item-icon { background-color: #f4eef6; color: #8e44ad; }
.config-item:hover .color-bunkering-fuel .item-icon { background-color: #8e44ad; }

/* System Configuration - Danger */
.color-system-config { border-top-color: #e74c3c; }
.color-system-config .item-icon { background-color: #fdedec; color: #e74c3c; }
.config-item:hover .color-system-config .item-icon { background-color: #e74c3c; }

/* Events - Dark */
.color-events { border-top-color: #2c3e50; }
.color-events .item-icon { background-color: #ebedef; color: #2c3e50; }
.config-item:hover .color-events .item-icon { background-color: #2c3e50; }

/* General - Primary */
.color-general { border-top-color: #ff69b4; }
.color-general .item-icon { background-color: #fff0f7; color: #ff69b4; }
.config-item:hover .color-general .item-icon { background-color: #ff69b4; }

</style>
